import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Button, Popover } from '@highlight/ui'
import { Popover as BasePopover } from '@base-ui/react/popover'
import { X } from 'lucide-react'
import type { StagedActionEditTag, StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { useEditOptions } from './edit-options-context'
import { ActionTagBadgeCollection } from './badge-collection'
import { resolveMultiTextSubmitValues, seedMultiTextItems } from './multi-text-items'

interface EditableMultiTextProps {
  keyName: string
  tags: Array<StagedActionView['tags'][number]>
  editTag: StagedActionEditTag
}

export function EditableMultiText({ keyName, tags, editTag }: EditableMultiTextProps) {
  const { onUpdate, isUpdating } = useEditOptions()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Source of truth for both the popover list and what we submit. Stateless by
  // design (see seedMultiTextItems): seeds names/emails from `editTag.options`
  // for divergent tags like Guest, else from the view tags' display values.
  // Tokens whose email can't be resolved are `unresolved` (display-only,
  // edit-blocked) until the edit-options query refetches.
  const items = useMemo(() => seedMultiTextItems(editTag.options, tags), [editTag.options, tags])

  // Resolved emails only — unresolved tokens are display-only and excluded.
  const currentValues = useMemo(() => resolveMultiTextSubmitValues(items), [items])

  // An unresolved token can't be faithfully submitted (omitting it would drop a
  // real attendee), so all edits are blocked until the refetch resolves it.
  const hasUnresolved = useMemo(() => items.some((item) => item.unresolved), [items])

  const didOpen = useRef(false)

  if (open) didOpen.current = true
  if (!open && !isUpdating) didOpen.current = false

  const popoverOpen = open || (isUpdating && didOpen.current)

  const handleOpen = useCallback(() => {
    setInputValue('')
    setOpen(true)
  }, [])

  const maxItems = editTag.constraints?.maxItems
  const atMaxItems = maxItems != null && currentValues.length >= maxItems
  const minItems = editTag.constraints?.minItems
  const atMinItems = minItems != null && currentValues.length <= minItems

  const addValue = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    if (isUpdating) return
    if (hasUnresolved) return
    if (atMaxItems) return
    // For divergent tags (e.g. Guest) the popover shows labels (names) while
    // currentValues holds submitted values (emails). Reject input matching
    // either, so retyping an existing guest doesn't append a literal name.
    if (items.some((item) => item.value === trimmed || item.label === trimmed)) return
    onUpdate({ tagEdits: { [keyName]: [...currentValues, trimmed] } })
    setInputValue('')
    inputRef.current?.focus()
  }, [inputValue, isUpdating, hasUnresolved, atMaxItems, items, currentValues, onUpdate, keyName])

  const removeValue = useCallback(
    (value: string) => {
      if (isUpdating) return
      if (hasUnresolved) return
      if (atMinItems) return
      onUpdate({ tagEdits: { [keyName]: currentValues.filter((v) => v !== value) } })
    },
    [currentValues, onUpdate, keyName, atMinItems, isUpdating, hasUnresolved],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        addValue()
      }
    },
    [addValue],
  )

  return (
    <Popover open={popoverOpen} onOpenChange={(v) => !isUpdating && setOpen(v)}>
      <BasePopover.Trigger
        render={<div />}
        className={`flex flex-wrap gap-2 ${isUpdating ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
        onClick={handleOpen}
      >
        <ActionTagBadgeCollection keyName={keyName} tags={tags} editable />
      </BasePopover.Trigger>
      <Popover.Content
        sideOffset={4}
        disableAnchorTracking
        className="flex w-56 flex-col rounded-xl p-0 dark:outline-0"
      >
        <div className="flex items-center justify-between px-2 pt-1.5">
          <span className="text-foreground-secondary text-xs font-medium capitalize">{keyName}</span>
          {maxItems != null && (
            <span className="text-foreground-placeholder text-xs">
              {currentValues.length}/{maxItems}
            </span>
          )}
        </div>

        {items.length > 0 && (
          <div className="flex max-h-40 flex-col gap-0.5 overflow-y-auto p-1">
            {items.map((item) => (
              <div
                key={item.value || `unresolved:${item.label}`}
                className="text-foreground-secondary flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm"
              >
                <span className="flex-1 truncate">{item.label}</span>
                {!atMinItems && (
                  <button
                    type="button"
                    className="text-foreground-placeholder hover:text-foreground-secondary flex shrink-0 items-center justify-center"
                    onClick={() => removeValue(item.value)}
                    disabled={isUpdating || hasUnresolved}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1.5 px-2 py-1.5">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={editTag.placeholder || 'Add value...'}
            disabled={atMaxItems || isUpdating || hasUnresolved}
            className="bg-element text-foreground placeholder:text-foreground-disabled flex-1 rounded-lg border-0 px-2 py-1 text-sm outline-none"
          />
          <Button
            variant="ghost"
            size="xs"
            onClick={addValue}
            disabled={!inputValue.trim() || atMaxItems || isUpdating || hasUnresolved}
          >
            Add
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}
